<template>
    <div v-if="!authInitialized" class="app-loader">
        <div class="loader-content">
            <div class="loader-dots">
                <span></span>
                <span></span>
                <span></span>
            </div>
            <p class="loader-text">Loading workspace&hellip;</p>
        </div>
    </div>
    <div v-else>
        <div class="main-bg w-full" style="min-height: 100vh">
            <div v-if="authenticated && showSidebar && isMobile" class="sidebar-overlay" @click="toggleSidebar"></div>
            <div class="row">
                <div class="col-md-12" style="padding: 0" v-if="!authenticated || $route.path == '/privacy-policy'" key="public-view">
                    <ForgotForm v-if="$route.path == '/forgot-password'" />
                    <ResetForm v-else-if="$route.path == '/reset-password'" />
                    <PrivacyPolicy v-else-if="$route.path == '/privacy-policy'" />
                    <LoginForm v-else />
                </div>
                <div class="col-2" style="padding: 0px" v-if="authenticated && showSidebar && $route.path !== '/privacy-policy'" key="sidebar-view">
                    <Sidebar />
                </div>
                <div
                    v-if="$route.path !== '/privacy-policy'"
                    :class="[{ 'col-10': authenticated && showSidebar }, { 'col-md-12': !authenticated || !showSidebar }]"
                    style="padding: 0px; display: flex; flex-direction: column"
                >
                    <Header v-if="authenticated" />
                    <div class="" style="flex: 1" v-if="authenticated">
                        <div class="py-3" style="height: 100%">
                            <div class="container-fluid">
                                <transition name="fade-slide" mode="out-in">
                                    <router-view v-if="authenticated" style="padding: 0px" />
                                </transition>
                            </div>
                        </div>
                    </div>
                    <div class="border-top text-muted bg-white small" v-if="authenticated" style="border-color: #e2e8f0 !important">
                        <div class="container">
                            <div class="row">
                                <div class="col-12 p-3 text-center" style="color: #94a3b8; font-size: 12px"><img src="@/assets/logo.png" alt="Swift HRMS" style="width: 18px; height: 18px; vertical-align: middle; margin-right: 6px; margin-top: -2px; object-fit: contain;" />&copy; {{ new Date().getFullYear() }} Swift HRMS | Swift Business Solutions. All rights reserved.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- AI HR Chatbot -->
            <ChatBot v-if="authenticated" />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex"
import Sidebar from "./components/layout/Sidebar.vue"
import Header from "./components/layout/Header.vue"
import LoginForm from "./components/layout/LoginForm.vue"
import ForgotForm from "./components/layout/ForgotForm.vue"
import ResetForm from "./components/layout/ResetForm.vue"
import PrivacyPolicy from "./pages/privacy-policy.vue"
import ChatBot from "./components/layout/ChatBot.vue"

export default {
    name: "App",
    computed: {
        ...mapGetters({
            authenticated: "auth/authenticated",
        }),
        ...mapState("auth", ["authInitialized"]),
        ...mapState("auth", ["user"]),
        ...mapState("misc", ["showSidebar"]),
        isMobile() {
            return window.innerWidth <= 768
        },
    },
    components: { Sidebar, ForgotForm, ResetForm, LoginForm, Header, PrivacyPolicy, ChatBot },
    data() {
        return { loggingOut: false }
    },
    methods: {
        ...mapActions("auth", ["initAuth"]),
        ...mapActions("auth", ["logout"]),
        ...mapActions("misc", ["fetchDashboardStatsMasters", "fetchAttendanceInfo", "setData"]),
        toggleSidebar() {
            this.setData({
                showSidebar: false,
            })
        },
        async logout() {
            this.loggingOut = true
            await this.logout()
            window.location.href = "/login"
        },
    },
    watch: {
        $route(e) {
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            })
        },
        authenticated(newVal) {
            if (newVal) {
                this.fetchDashboardStatsMasters()
                this.fetchAttendanceInfo()
            }
        },
    },
    async mounted() {
        const result = await this.initAuth()
        if (result) {
            await this.fetchAttendanceInfo()
        }
        if (this.authenticated) {
            this.fetchDashboardStatsMasters()
        }
    },
}
</script>

<style>
html, body {
    overflow-x: hidden;
    scroll-behavior: smooth;
}
#app {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #334155;
    margin-top: 0px;
    width: 100%;
    overflow-x: hidden;
    line-height: 1.6;
    letter-spacing: -0.01em;
}
body {
    font-size: 0.875em;
    background: #f1f5f9;
}
* {
    box-sizing: border-box;
}
.main-bg {
    background: #f1f5f9;
    overflow-x: hidden;
    width: 100%;
}

/* Smooth route transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-slide-enter {
    opacity: 0;
    transform: translateY(8px);
}
.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

/* Global scrollbar styling */
::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}
::-webkit-scrollbar-track {
    background: transparent;
}
::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
}
::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Global link reset */
a {
    text-decoration: none;
    color: inherit;
    transition: color 0.2s ease;
}

/* Selection styling */
::selection {
    background: rgba(0, 94, 137, 0.15);
    color: #003d5c;
}

/* ---- Tablet (768px) ---- */
@media (max-width: 768px) {
    .col-2 {
        /* Sidebar is position:fixed on mobile, so its wrapper should not take flow space */
        position: fixed !important;
        width: 80% !important;
        max-width: 300px !important;
        flex: 0 0 0 !important;
        padding: 0 !important;
        z-index: 100;
        height: 100vh;
        top: 0;
        left: 0;
    }
    .col-10 {
        width: 100% !important;
        max-width: 100%;
        flex: 0 0 100%;
    }
    /* Content always full width on mobile */
    .col-md-12 {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }
}

/* ---- Small mobile ---- */
@media (max-width: 480px) {
    .container-fluid {
        padding-left: 12px !important;
        padding-right: 12px !important;
    }
}
.sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(15, 23, 42, 0.4);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 99;
    animation: fadeIn 0.3s ease-in-out forwards;
}
@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

/* ---- Full-page app loader ---- */
.app-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: #f1f5f9;
}
.loader-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    animation: fadeIn 0.4s ease;
}
.loader-dots {
    display: flex;
    gap: 8px;
}
.loader-dots span {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #005e89;
    animation: dotPulse 1.4s ease-in-out infinite;
}
.loader-dots span:nth-child(2) {
    animation-delay: 0.15s;
}
.loader-dots span:nth-child(3) {
    animation-delay: 0.3s;
}
@keyframes dotPulse {
    0%, 80%, 100% {
        transform: scale(0.4);
        opacity: 0.3;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}
.loader-text {
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
    letter-spacing: 0.01em;
    margin: 0;
}

/* ---- Modern Input Styling ---- */
input[type="text"],
input[type="email"],
input[type="password"],
input[type="number"],
input[type="date"],
input[type="time"],
input[type="tel"],
input[type="url"],
input[type="search"],
textarea,
select,
.form-control {
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 6px !important;
    padding: 9px 12px !important;
    font-size: 13px !important;
    font-family: inherit !important;
    background-color: #ffffff !important;
    color: #1e293b !important;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;
}

input[type="text"]:hover,
input[type="email"]:hover,
input[type="password"]:hover,
input[type="number"]:hover,
input[type="date"]:hover,
input[type="time"]:hover,
input[type="tel"]:hover,
input[type="url"]:hover,
input[type="search"]:hover,
textarea:hover,
select:hover,
.form-control:hover {
    border-color: #cbd5e1 !important;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05) !important;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="password"]:focus,
input[type="number"]:focus,
input[type="date"]:focus,
input[type="time"]:focus,
input[type="tel"]:focus,
input[type="url"]:focus,
input[type="search"]:focus,
textarea:focus,
select:focus,
.form-control:focus {
    border-color: #005e89 !important;
    box-shadow: 0 0 0 3px rgba(0, 94, 137, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
    outline: none !important;
}

input:disabled,
textarea:disabled,
select:disabled,
.form-control:disabled {
    background-color: #f8fafc !important;
    border-color: #e2e8f0 !important;
    color: #94a3b8 !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
}

input[readonly],
textarea[readonly],
select[readonly],
.form-control[readonly] {
    background-color: #f1f5f9 !important;
    border-color: #e2e8f0 !important;
    color: #64748b !important;
}

/* Small form control variant */
.form-control-sm {
    padding: 7px 10px !important;
    font-size: 12px !important;
}
</style>
